import {
  Box,
  Button,
  Chip,
  Drawer,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Tooltip,
  useTheme,
} from "@mui/material";
import {
  ArrowBack,
  Menu as MenuIcon,
  Settings,
  LogoutSharp,
} from "@mui/icons-material";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  CHCM_LOGO_MARK_WHITE_URL,
  TOP_BAR_LOGO_HEIGHT_PX,
  SHELL_TOP_BAR_PX,
} from "config";
import { useViewAs } from "hooks/useViewAs";
import useNavCatalog from "hooks/useNavCatalog";
import useResponsive from "hooks/useResponsive";
import { catalogOffers, isNavPathActive } from "utils/navModel";
import ShellSidebar, {
  ShellNavButtonRow,
  ShellSidebarColumn,
} from "components/shell/ShellSidebar";
import TopBarAccount from "components/shell/TopBarAccount";
import { useGetSecureUnreadCountQuery } from "features/secureUploads/secureUploads";

interface ClientViewHeaderProps {
  onBackToUserManagement: () => void;
  children?: React.ReactNode;
}

export default function ClientViewHeader({
  onBackToUserManagement,
  children,
}: ClientViewHeaderProps) {
  const theme = useTheme();
  const { isCardMode: isMobile, isPhone } = useResponsive();
  const { userId } = useParams<{ userId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { isClientView, displayName } = useViewAs();

  const basePath = `/client-view/${userId ?? ""}`;
  const { catalog } = useNavCatalog();
  const showsSecureExchange = catalogOffers(catalog, "secureExchange");
  const { data: secureUnread } = useGetSecureUnreadCountQuery(undefined, {
    skip: !isClientView || !showsSecureExchange,
  });
  const secureUnreadCount = secureUnread?.unread ?? 0;

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  // As a link does: the page already open is replaced, not stacked.
  const goTo = (path: string) =>
    navigate(path, { replace: path === location.pathname });

  const closeDrawerAndNavigate = (path: string) => {
    setDrawerOpen(false);
    goTo(path);
  };

  // A window grown past the drawer's width keeps the column instead.
  useEffect(() => {
    if (!isMobile) setDrawerOpen(false);
  }, [isMobile]);

  if (!isClientView || !userId) return null;

  const welcomeLabel = displayName?.trim() || "Client";

  const shellHeaderMenuSlotProps = {
    root: {
      sx: { zIndex: theme.zIndex.tooltip },
    },
  };

  const userMenuPaperSx = {
    mt: 1.25,
    minWidth: 220,
    borderRadius: 1,
    border: "1px solid rgba(0,0,0,0.2)",
    boxShadow: "0px 8px 24px rgba(0,0,0,0.12)",
  };

  return (
    <>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <Box
          component="header"
          sx={{
            width: "100%",
            height: SHELL_TOP_BAR_PX,
            minHeight: SHELL_TOP_BAR_PX,
            maxHeight: SHELL_TOP_BAR_PX,
            flexShrink: 0,
            boxSizing: "border-box",
            bgcolor: "primary.main",
            boxShadow: theme.palette.calibrate.topBarShadow,
            display: "flex",
            alignItems: "center",
            zIndex: theme.zIndex.appBar,
          }}
        >
          <Toolbar
            disableGutters
            sx={{
              minHeight: SHELL_TOP_BAR_PX,
              maxHeight: SHELL_TOP_BAR_PX,
              width: "100%",
              px: 0,
              display: "flex",
              alignItems: "center",
              gap: 1,
              boxSizing: "border-box",
            }}
          >
            {isMobile && (
              <IconButton
                edge="start"
                sx={{ color: "#fff", ml: 0.5 }}
                aria-label="open menu"
                onClick={() => setDrawerOpen(true)}
              >
                <MenuIcon />
              </IconButton>
            )}
            <Link to={basePath} style={{ display: "flex", flexShrink: 0 }}>
              <Box
                component="img"
                src={CHCM_LOGO_MARK_WHITE_URL}
                alt="Calibrate HCM"
                sx={{
                  height: TOP_BAR_LOGO_HEIGHT_PX,
                  minHeight: TOP_BAR_LOGO_HEIGHT_PX,
                  maxHeight: TOP_BAR_LOGO_HEIGHT_PX,
                  width: "auto",
                  ml: "10px",
                  display: "block",
                  objectFit: "contain",
                  flexShrink: 0,
                }}
              />
            </Link>
            {/* On a phone the bar also carries the preview badge; the arrow
                alone still says "out of here". */}
            {isPhone ? (
              <Tooltip title="Back to Client & User Setup">
                <IconButton
                  size="small"
                  aria-label="Back to Client & User Setup"
                  onClick={onBackToUserManagement}
                  sx={{ color: "#fff", flexShrink: 0 }}
                >
                  <ArrowBack fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : (
              <Button
                size="small"
                startIcon={<ArrowBack sx={{ color: "#fff" }} />}
                onClick={onBackToUserManagement}
                sx={{
                  textTransform: "none",
                  ...theme.typography.calibrateShell,
                  fontWeight: 600,
                  color: "#fff",
                  flexShrink: 0,
                }}
              >
                Back to Client & User Setup
              </Button>
            )}
            <Tooltip title="Read-only preview. Nothing you do here changes this user's data.">
              <Chip
                label={
                  isMobile
                    ? "Preview"
                    : `Client View (read-only) · ${displayName || "this client"}`
                }
                size="small"
                sx={{
                  height: 28,
                  fontWeight: 600,
                  fontFamily: theme.typography.calibrateShell.fontFamily,
                  letterSpacing: theme.typography.calibrateShell.letterSpacing,
                  bgcolor: "rgba(255,255,255,0.2)",
                  color: "#fff",
                  "& .MuiChip-label": { px: 1.25 },
                  flexShrink: 0,
                  maxWidth: { xs: 88, sm: 280 },
                }}
              />
            </Tooltip>
            <Box sx={{ flexGrow: 1, minWidth: 0 }} />
            <Box sx={{ mr: "10px", minWidth: 0, display: "flex" }}>
              <TopBarAccount
                name={welcomeLabel}
                menuId="client-view-user-menu"
                menuOpen={menuOpen}
                onOpenMenu={setAnchorEl}
              />
            </Box>
            <Menu
              id="client-view-user-menu"
              anchorEl={anchorEl}
              open={menuOpen}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              slotProps={shellHeaderMenuSlotProps}
              PaperProps={{ sx: userMenuPaperSx }}
            >
              <MenuItem
                onClick={() => {
                  setAnchorEl(null);
                  navigate(`${basePath}/account-settings`);
                }}
                sx={{
                  py: 1,
                  "&:hover": { bgcolor: "rgba(49, 129, 159, 0.1)" },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Settings sx={{ fontSize: 20, color: "text.primary" }} />
                </ListItemIcon>
                <ListItemText
                  primary="Account Settings"
                  primaryTypographyProps={{
                    fontSize: 14,
                    fontFamily: theme.typography.fontFamily,
                  }}
                />
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setAnchorEl(null);
                  onBackToUserManagement();
                }}
                sx={{
                  py: 1,
                  "&:hover": { bgcolor: "rgba(255, 218, 218, 0.8)" },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <LogoutSharp sx={{ fontSize: 20, color: "error.main" }} />
                </ListItemIcon>
                <ListItemText
                  primary="Exit client view"
                  primaryTypographyProps={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: "error.main",
                  }}
                />
              </MenuItem>
            </Menu>
          </Toolbar>
        </Box>

        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "row",
            alignItems: "stretch",
            minHeight: 0,
            minWidth: 0,
            overflow: "hidden",
          }}
        >
          {!isMobile && (
            <ShellSidebarColumn>
              <ShellSidebar
                catalog={catalog}
                pathname={location.pathname}
                onNavigate={goTo}
                unreadCount={secureUnreadCount}
                ariaLabel="Client view navigation"
                scrollKey="client-view"
              />
            </ShellSidebarColumn>
          )}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              bgcolor: "background.default",
            }}
          >
            {children}
          </Box>
        </Box>
      </Box>

      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{
          "& .MuiDrawer-paper": {
            width: 280,
            boxSizing: "border-box",
            bgcolor: "primary.main",
            height: "100%",
            minHeight: "100%",
            backgroundImage: "none",
            overflow: "hidden",
          },
        }}
      >
        <ShellSidebar
          drawer
          catalog={catalog}
          pathname={location.pathname}
          onNavigate={closeDrawerAndNavigate}
          unreadCount={secureUnreadCount}
          ariaLabel="Client view navigation"
          scrollKey="client-view-drawer"
          footer={
            <ShellNavButtonRow
              icon={Settings}
              label="Account settings"
              active={isNavPathActive(
                location.pathname,
                `${basePath}/account-settings`,
              )}
              onClick={() =>
                closeDrawerAndNavigate(`${basePath}/account-settings`)
              }
            />
          }
        />
      </Drawer>
    </>
  );
}
